import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./guards/auth.guard";

// Services
import { SSOsResolverService } from "./pages/login/ssos-resolver.service";
import { CustomerSettingsResolverService } from "./pages/login/customer-settings-resolver.service";

// Constants
import { Constants } from "./constants/constants";
import { environment } from "../environments/environment";
import { HelpLinks } from "./constants/help-links";

const authRoutes = [
    {
        path: Constants.urls.login,
        loadComponent: () => import("./pages/login/login.component").then(m => m.LoginComponent),
        resolve: {
            ssos: SSOsResolverService,
            settings: CustomerSettingsResolverService
        }
    },
    {
        path: Constants.urls.forgot_password,
        loadComponent: () =>
            import("./pages/forgot-password/forgot-password.component").then(m => m.ForgotPasswordComponent)
    },
    {
        path: Constants.urls.reset,
        loadComponent: () =>
            import("./pages/reset-password/reset-password.component").then(m => m.ResetPasswordComponent)
    },
    {
        path: Constants.urls.set_password,
        loadComponent: () => import("./pages/set-password/set-password.component").then(m => m.SetPasswordComponent),
        canLoad: [AuthGuard]
    },
    {
        path: Constants.urls.search,
        loadComponent: () => import("./pages/search/search.component").then(m => m.SearchComponent),
        canActivate: [AuthGuard]
    }
];

const componentRoutes = [
    ...authRoutes,
    {
        path: Constants.urls.four_o_four,
        loadComponent: () => import("./pages/not-found/not-found.component").then(m => m.NotFoundComponent),
        canActivate: [AuthGuard]
    },
    {
        path: Constants.urls.not_found,
        loadComponent: () => import("./pages/not-found/not-found.component").then(m => m.NotFoundComponent),
        canActivate: [AuthGuard]
    },
    {
        path: "style-guide",
        loadComponent: () => import("./pages/style-guide/style-guide.component").then(m => m.StyleGuideComponent),
        canActivate: [AuthGuard]
    },
    {
        path: Constants.urls.dashboard,
        loadComponent: () => import("./pages/dashboard/dashboard.component").then(m => m.DashboardComponent),
        canActivate: [AuthGuard],
        data: {
            help: [
                {
                    menu: "HELP_MENU.DASH",
                    link: HelpLinks.HELP_MENU.DASH
                }
            ]
        }
    }
];

export const appRoutes: Routes = [
    ...componentRoutes,
    {
        path: Constants.urls.incidents,
        loadChildren: () => import("./pages/incidents/incidents.routing").then(m => m.IncidentsRouting)
    },
    {
        path: Constants.urls.grids,
        loadChildren: () => import("./pages/grids/grids.routing").then(m => m.GridsRouting)
    },
    {
        path: Constants.urls.clusters,
        loadChildren: () => import("./pages/clusters/clusters.routing").then(m => m.ClustersRouting)
    },
    {
        path: Constants.urls.zecs,
        loadChildren: () => import("./pages/zecs/zecs.routing").then(m => m.ZecsRouting)
    },
    {
        path: Constants.urls.sources,
        loadChildren: () => import("./pages/sources/sources.routing").then(m => m.SourcesRouting)
    },
    {
        path: Constants.urls.channels,
        loadChildren: () => import("./pages/channels/channels.routing").then(m => m.ChannelsRouting)
    },
    {
        path: Constants.urls.targets,
        loadChildren: () => import("./pages/targets/targets.routing").then(m => m.TargetsRouting)
    },
    {
        path: Constants.urls.liveevents,
        loadChildren: () =>
            import("./modules/live-events/live-events-routing.module").then(m => m.LiveEventsRoutingModule)
    },
    {
        path: Constants.urls.accounts,
        loadChildren: () => import("./pages/accounts/accounts.routing").then(m => m.AccountsRouting)
    },
    {
        path: Constants.urls.adminBase,
        redirectTo: Constants.urls.announcements_configurations,
        pathMatch: "full"
    },
    {
        path: Constants.urls.io_servers,
        loadChildren: () => import("./pages/io-servers/io-servers.routing").then(m => m.IoServersRouting)
    },
    {
        path: Constants.urls.transformationBase,
        redirectTo: Constants.urls.transformation.transcoding_profiles,
        pathMatch: "full"
    },
    {
        path: Constants.urls.transformation.transcoding_profiles,
        loadChildren: () =>
            import("./pages/transcoding-profiles/transcoding-profiles.routing").then(m => m.TranscodingProfilesRouting)
    },
    {
        path: Constants.urls.transformation.pid_mapping_profiles,
        loadChildren: () => import("./pages/pid-mappings/pid-mappings.routing").then(m => m.PidMappingsRouting)
    },
    {
        path: Constants.urls.transformation.scte35_profiles,
        loadChildren: () =>
            import("./pages/scte35-profiles/scte35-profiles.routing").then(m => m.Scte35ProfilesRouting),
        canActivate: [AuthGuard]
    },
    {
        path: Constants.urls.automation,
        loadChildren: () => import("./pages/automation/automation.routing").then(m => m.AutomationRouting)
    },
    {
        path: Constants.urls.reports,
        loadChildren: () => import("./pages/reports/reports.routing").then(m => m.ReportsRouting)
    },
    {
        path: Constants.urls.announcements_configurations,
        loadChildren: () =>
            import("./pages/announcements-configurations/announcements-configurations.routing").then(
                m => m.AnnouncementsConfigurationsRouting
            )
    },
    {
        path: Constants.urls.remote_access,
        loadChildren: () => import("./pages/remote-access/remote-access.routing").then(m => m.RemoteAccessRouting)
    },
    {
        path: Constants.urls.maps,
        loadChildren: () => import("./pages/maps/maps.routing").then(m => m.MapsRouting)
    },
    {
        path: Constants.urls.networks,
        loadChildren: () => import("./pages/networks/networks.routing").then(m => m.NetworksRoutingModule)
    },
    {
        path: Constants.urls.multi_viewer,
        loadChildren: () => import("./pages/multi-viewers/multi-viewer.routing").then(m => m.MultiViewerRouting)
    },
    {
        path: Constants.urls.logsBase,
        redirectTo: Constants.urls.logs.events,
        pathMatch: "full"
    },
    {
        path: Constants.urls.logs.events,
        loadChildren: () => import("./pages/events/events.routing").then(m => m.EventsRouting)
    },
    {
        path: Constants.urls.logs.scte,
        loadChildren: () => import("./pages/scte/scte.routing").then(m => m.ScteRouting)
    },
    {
        path: Constants.urls.accountManagementBase,
        redirectTo: Constants.urls.accountManagement.myAccount,
        pathMatch: "full"
    },
    {
        path: Constants.urls.accountManagement.myAccount,
        loadChildren: () =>
            import("./pages/account-management/my-account/my-account.routing").then(m => m.MyAccountRouting)
    },
    {
        path: Constants.urls.accountManagement.sso,
        loadChildren: () => import("./pages/account-management/sso/ssos.routing").then(m => m.SSOsRouting)
    },
    {
        path: Constants.urls.accountManagement.users,
        loadChildren: () => import("./pages/account-management/users/users.routing").then(m => m.UsersRouting)
    },
    {
        path: Constants.urls.accountManagement.userGroups,
        loadChildren: () =>
            import("./pages/account-management/user-groups/user-groups.routing").then(m => m.UserGroupsRouting)
    },
    {
        path: Constants.urls.accountManagement.roles,
        loadChildren: () => import("./pages/account-management/roles/roles.routing").then(m => m.RolesRouting)
    },
    {
        path: Constants.urls.configurationBase,
        redirectTo: Constants.urls.configuration.tags,
        pathMatch: "full"
    },
    {
        path: Constants.urls.configuration.tags,
        loadChildren: () => import("./pages/configuration/tags/tags.routing").then(m => m.TagsRouting)
    },
    {
        path: Constants.urls.configuration.apiKeys,
        loadChildren: () => import("./pages/configuration/api-keys/api-keys.routing").then(m => m.ApiKeysRouting)
    },
    {
        path: Constants.urls.configuration.sshKeys,
        loadChildren: () => import("./pages/configuration/ssh-keys/ssh-keys.routing").then(m => m.SshKeysRouting)
    },
    {
        path: Constants.urls.configuration.aws,
        loadChildren: () =>
            import("./pages/configuration/aws-accounts/aws-accounts.routing").then(m => m.AwsAccountsRouting)
    },
    {
        path: Constants.urls.configuration.azure,
        loadChildren: () =>
            import("./pages/configuration/azure-accounts/azure-accounts.routing").then(m => m.AzureAccountsRouting)
    },
    {
        path: Constants.urls.configuration.gcp,
        loadChildren: () =>
            import("./pages/configuration/gcp-accounts/gcp-accounts.routing").then(m => m.GcpAccountsRouting)
    },
    {
        path: Constants.urls.configuration.linode,
        loadChildren: () =>
            import("./pages/configuration/linode-accounts/linode-accounts.routing").then(m => m.LinodeAccountsRouting)
    },
    {
        path: Constants.urls.configuration.tams,
        loadChildren: () => import("./pages/configuration/tams/tams.routing").then(m => m.TamsRouting)
    },
    {
        path: Constants.urls.configuration.notifications,
        loadChildren: () =>
            import("./pages/configuration/notifications/notifications.routing").then(m => m.NotificationsRouting)
    },
    {
        path: Constants.urls.configuration.eventsManagement,
        loadChildren: () =>
            import("./pages/configuration/events-management/events-management.routing").then(
                m => m.EventsManagementRouting
            )
    },
    {
        path: Constants.urls.configuration.filterProfiles,
        loadChildren: () => import("./pages/configuration/filters/filters.routing").then(m => m.FiltersRouting)
    },
    {
        path: Constants.urls.configuration.customImage,
        loadChildren: () =>
            import("./pages/configuration/custom-image/custom-image.routing").then(m => m.CustomImageRouting)
    },
    {
        path: Constants.urls.esniBase,
        loadChildren: () => import("./pages/esni/esni.routing").then(m => m.EsniRouting),
        canLoad: [AuthGuard]
    },
    {
        path: Constants.urls.sharedBase,
        loadChildren: () => import("./pages/shared/shared-pages.routing").then(m => m.SharedPagesRouting)
    },
    {
        path: "",
        loadComponent: () => import("./pages/dashboard/dashboard.component").then(m => m.DashboardComponent),
        pathMatch: "full",
        canActivate: [AuthGuard]
    },
    {
        path: "**",
        loadComponent: () => import("./pages/not-found/not-found.component").then(m => m.NotFoundComponent),
        pathMatch: "full",
        canActivate: [AuthGuard]
    }
];

if (environment.shouldShowComponentStories) {
    appRoutes.push({
        path: "component-stories",
        loadChildren: () =>
            import("./pages/components-stories/components-stories-routing.module").then(
                m => m.ComponentsStoriesRoutingModule
            ),
        canActivate: [AuthGuard]
    });
}

@NgModule({
    imports: [
        RouterModule.forRoot(appRoutes, {
            useHash: false,
            enableTracing: false, // <-- debugging purposes only
            anchorScrolling: "enabled",
            onSameUrlNavigation: "reload",
            scrollPositionRestoration: "enabled",
            bindToComponentInputs: true
            // paramsInheritanceStrategy: "always"
        })
    ],
    exports: [RouterModule]
})
export class AppRouting {}
